import { Button, Stack } from '@form-factory/chatbot-ui';
import type { ChatUserType } from '@form-factory/schema';
import type {
  EfCreateMemberDTO,
  EfIntCreateMembershipDTO,
  WithClubId,
} from '@form-factory/schema/contracts';
import { EFitnessGender } from '@form-factory/schema/enums';
import { DateFormat, formatUtils } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Checkbox, Input, type TypedFormState } from '@utima/ui-informed';
import type { FieldState } from 'informed';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import MultipleStepForm from '@/components/multistepForm/MultipleStepForm';
import StepForm from '@/components/multistepForm/StepForm';
import { getLocationName } from '@/constants/Locations';
import { useChatConfig } from '@/contexts/ChatConfigProvider';
import { useEfErrorHandler } from '@/hooks/useEfErrorHandler';
import { aiCoreApi } from '@/services/aiCoreClient';
import { membershipDefinitionsKeys } from '@/services/queryClient';
import { useBoundStore } from '@/store/store';

import { EfErrorAlert } from '../alerts/EfErrorAlert';
import { ClubSelect } from '../eFitness/ClubSelect';
import { Loader } from '../loader/Loader';
import { NativeFormSelect } from '../nativeFormSelect/NativeFormSelect';

type FormValues = EfCreateMemberDTO & {
  club: string;
  consents?: Record<string, boolean>;
};

export function MemberRegistration() {
  const { errors, handleError, resetErrors } = useEfErrorHandler();
  const { t } = useLingui();
  const { chatId } = useChatConfig();
  const queryClient = useQueryClient();
  const { login, popRoute } = useBoundStore(
    useShallow(state => ({
      login: state.login,
      pushRoute: state.pushRoute,
      popRoute: state.popRoute,
    })),
  );

  // TODO: This is here as a workaround to show long error message, since @utima/ui does not support long error messages
  //       as they clip into the inputs. This should be removed once the issue is fixed in the library.
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined,
  );
  const [clubId, setClubId] = useState<string>();
  const [selectedConsents, setSelectedConsents] = useState<Set<number>>(
    new Set(),
  );

  const validatePassword = (password: string) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!"#$%&()*,.:<>?@^{|}]/.test(password);

    if (
      password.length < minLength ||
      !hasUpperCase ||
      !hasNumber ||
      !hasSpecialChar
    ) {
      setPasswordError(
        t`Password must contain: Minimum 8 characters, at least one uppercase letter, one number and one special character.`,
      );

      return false;
    }

    setPasswordError(undefined);
  };

  // Using the API call
  const membershipDefinitions = useQuery({
    queryKey: ['membershipDefinitions', clubId],
    queryFn: async () => {
      if (!clubId) return [];

      return aiCoreApi.eFitness.membershipDefinitions.memberships({
        clubId: clubId,
      });
    },
  });

  const freePassId = membershipDefinitions.data?.find(
    membership => membership.name === 'Free pass',
  )?.id;

  const accessToken = useBoundStore(state => state.accessToken);

  const { data: consentDefinitions, isFetching: isFetchingConsents } = useQuery(
    {
      queryKey: membershipDefinitionsKeys.consents(),
      queryFn: async () => {
        if (!clubId || !freePassId) return [];

        return aiCoreApi.eFitness.membershipDefinitions.consents({
          clubId: clubId,
          id: freePassId,
        });
      },
      retry: 0,
      enabled: !!freePassId && !!accessToken,
    },
  );

  useEffect(() => {
    if (clubId) {
      queryClient.invalidateQueries({
        queryKey: membershipDefinitionsKeys.consents(),
      });
    }
  }, [clubId, queryClient, consentDefinitions]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (formState: TypedFormState<FormValues>) => {
      return aiCoreApi.eFitness.members.create({
        ...formState.values,
        clubId: formState.values.club,
        gender: Number(formState.values.gender),
      });
    },
  });

  const mutation = useMutation({
    mutationFn: async (
      params: WithClubId<EfIntCreateMembershipDTO> & {
        authTokenOverride?: string;
      },
    ) => {
      return aiCoreApi.eFitness.memberships.create(
        {
          chatId: params.chatId,
          clubId: params.clubId,
          membershipDefinitionId: params.membershipDefinitionId,
          userInfo: params.userInfo,
          agreementFrom: params.agreementFrom,
          selectedConsentIds: params.selectedConsentIds,
          selectedServiceIds: params.selectedServiceIds,
        },
        params.authTokenOverride
          ? {
              headers: {
                Authorization: `Bearer ${params.authTokenOverride}`,
              },
            }
          : undefined,
      );
    },
  });

  const handleSubmit = async (formState: TypedFormState<FormValues>) => {
    resetErrors();

    try {
      const { token, user, eFitness } = await mutateAsync(formState);

      // First login with registration token so we can call eFitness login.
      const userWithClubId = {
        ...(user as unknown as ChatUserType),
        clubId: (user as any)?.clubId ?? clubId,
      } as ChatUserType;
      await login(userWithClubId, token, eFitness);

      // Immediately perform eFitness login to get a JWT that embeds eFitness in the payload.
      const efLogin = await aiCoreApi.eFitness.auth.login({
        clubId: clubId!,
        login: String(formState.values.email),
        password: String(formState.values.password),
      });

      // Update auth state with proper JWT containing eFitness context.
      const userWithClubId2 = {
        ...(efLogin.user as unknown as ChatUserType),
        clubId: (efLogin.user as any)?.clubId ?? clubId,
      } as ChatUserType;
      await login(userWithClubId2, efLogin.token, efLogin.eFitness);

      if (clubId && freePassId) {
        await mutation.mutateAsync({
          chatId,
          clubId: clubId,
          membershipDefinitionId: freePassId,
          agreementFrom: new Date().toISOString(),
          selectedConsentIds: Array.from(selectedConsents),
          selectedServiceIds: [],
          discountCoupon: '',
          userInfo: {
            id: user!.externalId!,
            name: `${user!.firstName} ${user!.lastName}`,
            email: user!.email!,
            club: getLocationName(Number(clubId)),
            membershipName: 'Free pass',
          },
          // Ensure server receives a token with eFitness in payload
          authTokenOverride: efLogin.token,
        });
      }
    } catch (err) {
      await handleError(err);
    }
  };

  return (
    <Stack title={t`Member registration`} onBack={() => popRoute()}>
      <EfErrorAlert errors={errors} />
      <MultipleStepForm showStepCounter onSubmit={handleSubmit}>
        <StepForm
          zodSchema={z
            .object({
              email: z.string().email(),
              phone: z.string().refine((val: string) => /^\d+$/.test(val), {
                message: t`Number must have 9 digits`,
              }),
            })
            .strict()}
          validateOn='change-submit'
          className='flex flex-col gap-5'
        >
          <ClubSelect
            required
            name='club'
            label={t`Club`}
            onChange={(fieldState: FieldState) => {
              const value = fieldState.value as string;
              if (value === '') return;
              setClubId(value);
            }}
          />
          <Input name='firstName' label={t`First name`} required />
          <Input required name='lastName' label={t`Last name`} />
          <Input name='email' type='email' required label={t`Email`} />
          <div className='flex flex-row justify-around gap-2'>
            <NativeFormSelect
              defaultValue='+420'
              required
              className='w-1/4'
              name='countryPhoneCode'
              label={t`Prefix`}
            >
              <option value='+420'>+420</option>
              <option value='+421'>+421</option>
              <option value='+385'>+385</option>
            </NativeFormSelect>
            <Input
              className='w-3/4'
              name='cellPhone'
              maxLength={9}
              zodItemSchema={z
                .string()
                .refine((val: string) => /^\d{9}$/.test(val), {
                  message: t`Number must have 9 digits`,
                })}
              required
              label={t`Phone number`}
            />
          </div>
          <Input
            name='password'
            passwordPreview
            type='password'
            min={8}
            validate={val => validatePassword(String(val))}
            required
            label={t`Password`}
          />
          {passwordError && (
            <p className='-bottom-3 -mt-3 text-xs text-chat-destructive animate-in fade-in'>
              {passwordError}
            </p>
          )}
          <Input
            name='confirmPassword'
            type='password'
            passwordPreview
            required
            label={t`Confirm password`}
            validate={(val, values) => {
              const password = values?.password;

              if (val !== password) {
                return t`Passwords do not match`;
              }
            }}
          />
          <Button
            size='lg'
            type='submit'
            disabled={isPending}
            loading={isPending}
          >
            <Trans>Next step</Trans>
          </Button>
        </StepForm>

        <StepForm
          zodSchema={z
            .object({
              gender: z.string().min(1, t`Gender is required`),
              birthday: z.string().min(1, t`Date of birth is required`),
              street: z.string().min(1, t`Street is required`),
              city: z.string().min(1, t`City is required`),
              postalCode: z
                .string()
                .refine((val: string) => /^\d{5}$/.test(val), {
                  message: t`ZIP code must have 5 digits`,
                }),
            })
            .strict()}
          validateOn='change-submit'
          className='flex flex-col gap-5'
        >
          <NativeFormSelect
            name='gender'
            defaultValue=''
            label={t`Gender`}
            required
          >
            <option value={EFitnessGender.MAN.toString()}>
              <Trans>Male</Trans>
            </option>
            <option value={EFitnessGender.WOMAN.toString()}>
              <Trans>Female</Trans>
            </option>
            <option value={EFitnessGender.OTHER.toString()}>
              <Trans>Other</Trans>
            </option>
          </NativeFormSelect>
          <Input
            label={t`Date of birth`}
            name='birthday'
            type='date'
            required
            max={new Date().toISOString().split('T')[0]}
            zodItemSchema={z.string().refine(
              value => {
                const date = new Date(value);
                const today = new Date();
                const oneYearAgo = new Date(
                  today.getFullYear() - 1,
                  today.getMonth(),
                  today.getDate(),
                );

                return (
                  date.getTime() <= Date.now() &&
                  date.getTime() <= oneYearAgo.getTime()
                );
              },
              {
                message: t`You must be at least 1 year old`,
              },
            )}
            // @ts-expect-error Property 'mask' does not exist on type 'IntrinsicAttributes & InputProps'
            mask={value => formatUtils.date(value, DateFormat.DateInput)}
          />
          <Input name='street' label={t`Street`} required />
          <Input name='city' label={t`City`} required />
          <Input
            name='postalCode'
            maxLength={5}
            zodItemSchema={z
              .string()
              .refine((val: string) => /^\d{5}$/.test(val), {
                message: t`ZIP code must have 5 digits`,
              })}
            label={t`ZIP code`}
            required
          />

          <Button
            size='lg'
            type='submit'
            disabled={isPending}
            loading={isPending}
          >
            <Trans>Next step</Trans>
          </Button>
        </StepForm>

        <StepForm className='flex flex-col gap-5'>
          {isFetchingConsents ? (
            <Loader />
          ) : (
            <>
              <h3 className='mb-3 text-lg font-semibold'>{t`Consent Declarations`}</h3>
              {consentDefinitions
                ?.filter(
                  consent =>
                    (consent.content &&
                      Array.isArray(consent.links) &&
                      consent.links.length > 0) ||
                    consent.content !== null,
                )
                .map(consent => {
                  const [description] = consent.content.split(/\n\n|\//);

                  return (
                    <div key={consent.consentDefinitionId}>
                      <Checkbox
                        name={consent.consentDefinitionId.toString()}
                        required={consent.required}
                        variant='success'
                        description={description.trim() || consent.content}
                        className='flex flex-row'
                        onChange={(fieldState: FieldState) => {
                          const value = fieldState.value as boolean;

                          setSelectedConsents(prev => {
                            const newSet = new Set(prev);
                            if (value) {
                              newSet.add(consent.consentDefinitionId);
                            } else {
                              newSet.delete(consent.consentDefinitionId);
                            }

                            return newSet;
                          });
                        }}
                        validate={value => {
                          if (consent.required && !value) {
                            return t`Requried`;
                          }
                        }}
                      />
                      {consent?.links?.length > 0 && (
                        <a
                          href={consent?.links[0].url}
                          target='_blank'
                          rel='noreferrer'
                          className='text-sm text-chat-foreground underline'
                        >
                          {consent?.links[0].text}
                        </a>
                      )}
                    </div>
                  );
                })}

              {!consentDefinitions && (
                <p className='text-sm font-medium text-rose-600 animate-in fade-in slide-in-from-top'>
                  {t`Something went wrong. Please try again later`}
                </p>
              )}
            </>
          )}

          <Button
            size='lg'
            type='submit'
            disabled={isPending || !consentDefinitions}
            loading={isPending}
          >
            {t`Complete Registration`}
          </Button>
        </StepForm>
      </MultipleStepForm>
    </Stack>
  );
}
